import type { CustomerSegmentCriteria } from '../schemas/customer-segment.js';

/**
 * The customer-index projection that `matchesSegment` evaluates. A defensive
 * subset of `TenantCustomerIndexSchema` — every field optional so the matcher
 * is safe to run over partial/legacy docs. Timestamps are passed as **epoch
 * milliseconds**, not Firestore `Timestamp` objects, so this helper stays pure
 * and environment-agnostic (no SDK import); the caller converts once.
 */
export interface SegmentCustomer {
  readonly tierId?: string;
  readonly tags?: readonly string[];
  readonly lifetimeSpendSen?: number;
  readonly visits?: number;
  /** First-visit time in epoch ms. */
  readonly firstVisitAt?: number;
  /** Last-visit time in epoch ms. */
  readonly lastVisitAt?: number;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Evaluate the AND of every PRESENT condition in `criteria` against a single
 * customer-index projection. Pure + total (never throws).
 *
 * Design contract:
 *   - **Empty criteria matches everyone.** A condition that is absent (or an
 *     empty `tierIdIn` / `tagIdsAny` array) imposes no constraint.
 *   - **Missing customer fields fail closed sensibly:**
 *       · `lifetimeSpendSen` / `visits` absent → treated as `0` (so a
 *         `minSpend`/`minVisits` filter excludes them, a `maxSpend`/`maxVisits`
 *         filter includes them — the intuitive result).
 *       · `tierId` absent → no `tierIdIn` filter can match it (excluded).
 *       · `tags` absent → treated as `[]` (no `tagIdsAny` filter matches).
 *       · `lastVisitAt` / `firstVisitAt` absent → the recency filter that needs
 *         it cannot be satisfied, so the customer is excluded (never guess a
 *         win-back/welcome customer without the timestamp to prove it).
 *   - **Recency math (relative to `nowMs`):**
 *       · `inactiveDays` — last visit STRICTLY older than N days ago
 *         (`lastVisitAt < nowMs − N·day`). A customer whose last visit was
 *         exactly N days ago is NOT yet "inactive for N days".
 *       · `newWithinDays` — first visit within the last N days
 *         (`firstVisitAt >= nowMs − N·day`), inclusive of exactly N days ago.
 */
export function matchesSegment(
  customer: SegmentCustomer,
  criteria: CustomerSegmentCriteria,
  nowMs: number,
): boolean {
  if (criteria.tierIdIn && criteria.tierIdIn.length > 0) {
    if (customer.tierId === undefined || !criteria.tierIdIn.includes(customer.tierId)) {
      return false;
    }
  }

  if (criteria.tagIdsAny && criteria.tagIdsAny.length > 0) {
    const tags = customer.tags ?? [];
    if (!criteria.tagIdsAny.some((id) => tags.includes(id))) return false;
  }

  const spend = customer.lifetimeSpendSen ?? 0;
  if (criteria.minSpendSen !== undefined && spend < criteria.minSpendSen) return false;
  if (criteria.maxSpendSen !== undefined && spend > criteria.maxSpendSen) return false;

  const visits = customer.visits ?? 0;
  if (criteria.minVisits !== undefined && visits < criteria.minVisits) return false;
  if (criteria.maxVisits !== undefined && visits > criteria.maxVisits) return false;

  if (criteria.inactiveDays !== undefined) {
    if (customer.lastVisitAt === undefined) return false;
    const threshold = nowMs - criteria.inactiveDays * DAY_MS;
    if (customer.lastVisitAt >= threshold) return false;
  }

  if (criteria.newWithinDays !== undefined) {
    if (customer.firstVisitAt === undefined) return false;
    const threshold = nowMs - criteria.newWithinDays * DAY_MS;
    if (customer.firstVisitAt < threshold) return false;
  }

  return true;
}
